<template>
  <div class="gvt-panel" :class="{ 'gvt-panel--compact': compact }">
    <div v-if="showToolbar" class="gvt-panel__toolbar">
      <el-input
        v-model="searchText"
        :placeholder="searchPlaceholder"
        clearable
        :disabled="disabled"
        prefix-icon="el-icon-search"
        class="gvt-panel__search"
        @input="onSearchInput"
        @clear="onSearchInput"
      />
      <button
        type="button"
        class="gvt-panel__icon-btn"
        title="刷新"
        :disabled="disabled || treeLoading"
        @click="reloadTree"
      >
        <i class="el-icon-refresh" :class="{ 'gvt-panel__spin': treeLoading }" />
      </button>
      <button
        v-if="showSettings && canManageVehicles"
        type="button"
        class="gvt-panel__icon-btn gvt-panel__icon-btn--settings"
        title="车辆管理"
        :disabled="disabled"
        @click="goVehicleManage"
      >
        <i class="el-icon-setting" />
      </button>
      <slot name="toolbar-extra" />
    </div>

    <div v-loading="treeLoading" class="gvt-panel__tree-wrap" @click="onTreeWrapClick" @mousedown.prevent>
      <el-tree
        ref="garageTree"
        :data="displayTreeData"
        :show-checkbox="showCheckbox"
        node-key="id"
        highlight-current
        :indent="18"
        :default-expand-all="shouldExpandAll"
        :props="{ children: 'children', label: 'label', isLeaf: 'isLeaf', disabled: 'disabled' }"
        :render-content="renderTreeContent"
        class="gvt-panel__tree"
        @node-click="onTreeNodeClick"
        @check="onTreeCheck"
        @check-change="onTreeCheckChange"
      />
      <div v-if="!treeLoading && !displayTreeData.length" class="gvt-panel__empty">暂无车辆数据</div>
    </div>
  </div>
</template>

<script>
import {
  GARAGE_NODE_LOAD_MORE,
  applyElTreeExpandAll,
  collectGarageVehicleNodes,
  createGarageTreeLoader,
  filterGarageTreeByQuery,
  findGarageNodeById,
  isGarageGroupNode,
  renderGarageVehicleTreeIcon,
  resolveGarageVehicleColor
} from '@/utils/garageTree'
import { isLicensePlateFormatValid } from '@/utils/searchFilterValidate'
import { authState, hasPermission } from '@/utils/auth'

export default {
  name: 'GarageVehicleTreePanel',
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    enableTreeFetch: {
      type: Boolean,
      default: true
    },
    showCheckbox: {
      type: Boolean,
      default: false
    },
    showToolbar: {
      type: Boolean,
      default: true
    },
    showSettings: {
      type: Boolean,
      default: true
    },
    compact: {
      type: Boolean,
      default: false
    },
    /** 无搜索词时是否默认展开全部分组 */
    defaultExpandAll: {
      type: Boolean,
      default: true
    },
    searchPlaceholder: {
      type: String,
      default: '车牌号码'
    },
    garageTreeListUrl: {
      type: String,
      default: ''
    },
    garageTreeListUseRequest: {
      type: Boolean,
      default: false
    },
    garageTreeChildrenUrl: {
      type: String,
      default: ''
    },
    garageTreeChildrenUseRequest: {
      type: Boolean,
      default: false
    },
    vehicleManagePath: {
      type: String,
      default: '/vehicles'
    },
    externalSearchText: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      searchText: '',
      vehicleTreeData: [],
      treeLoading: false,
      garageLoadMoreLoading: false,
      selectedNode: null,
      suppressCheckChange: false,
      treeLoader: null,
      requestSequence: 0
    }
  },
  computed: {
    canManageVehicles() { return hasPermission('vehicle:list') },
    candidateScope() {
      return authState.user && hasPermission('vehicle:list') ? String(authState.user.id) : ''
    },
    displayTreeData() {
      const q = this.effectiveSearchText
      if (!q) return this.vehicleTreeData
      return filterGarageTreeByQuery(this.vehicleTreeData, q)
    },
    effectiveSearchText() {
      const internal = this.searchText != null ? String(this.searchText).trim() : ''
      if (internal) return internal
      const ext = this.externalSearchText != null ? String(this.externalSearchText).trim() : ''
      return ext
    },
    shouldExpandAll() {
      return this.defaultExpandAll || !!this.effectiveSearchText
    }
  },
  watch: {
    enableTreeFetch(val) {
      if (val) this.tryLoadTree()
      else {
        this.requestSequence++
        this.vehicleTreeData = []
        this.treeLoading = false
      }
    },
    candidateScope() {
      this.requestSequence++
      this.vehicleTreeData = []
      this.selectedNode = null
      this.treeLoading = false
      this.tryLoadTree()
    },
    effectiveSearchText() {
      this.syncSearchExpandState()
      this.tryLoadTree()
    },
    displayTreeData() {
      this.syncSearchExpandState()
    },
    treeLoading(loading) {
      if (!loading) this.syncSearchExpandState()
    },
    externalSearchText(val, oldVal) {
      const next = val != null ? String(val).trim() : ''
      const prev = oldVal != null ? String(oldVal).trim() : ''
      if (next !== prev) this.searchText = ''
    }
  },
  created() {
    this.treeLoader = createGarageTreeLoader({
      garageTreeListUrl: this.garageTreeListUrl,
      garageTreeListUseRequest: this.garageTreeListUseRequest,
      garageTreeChildrenUrl: this.garageTreeChildrenUrl,
      garageTreeChildrenUseRequest: this.garageTreeChildrenUseRequest
    })
  },
  mounted() {
    this.tryLoadTree()
  },
  beforeDestroy() {
    this.requestSequence++
  },
  methods: {
    tryLoadTree() {
      if (!this.enableTreeFetch) return
      this.reloadTree()
    },
    async reloadTree() {
      if (!this.enableTreeFetch || !this.treeLoader || !this.candidateScope) return
      const sequence = ++this.requestSequence
      const actor = authState.user
      const query = this.effectiveSearchText
      const isCurrent = () => sequence === this.requestSequence && actor === authState.user && query === this.effectiveSearchText
      this.treeLoading = true
      this.garageLoadMoreLoading = false
      try {
        const rows = await this.treeLoader.loadGarageTreeData(query)
        if (!isCurrent()) return
        this.vehicleTreeData = rows
        this.$emit('tree-loaded', this.vehicleTreeData)
      } catch (e) {
        if (!isCurrent()) return
        this.$message.error(e.msg || e.message || '车辆分组加载失败')
      } finally {
        if (sequence === this.requestSequence) this.treeLoading = false
      }
    },
    onSearchInput() {
      this.$emit('search-change', this.searchText)
      this.syncSearchExpandState()
    },
    syncSearchExpandState() {
      this.$nextTick(() => {
        applyElTreeExpandAll(this.$refs.garageTree, this.shouldExpandAll)
      })
    },
    goVehicleManage() {
      if (!this.canManageVehicles) return
      this.$emit('open-vehicle-manage')
      this.$router.push(this.vehicleManagePath || '/vehicles').catch(() => {})
    },
    renderTreeContent(h, { node, data }) {
      if (data && data.type === GARAGE_NODE_LOAD_MORE) {
        return h(
          'span',
          {
            class: 'gvt-tree__load-more',
            on: { click: (e) => { e.stopPropagation(); this.handleLoadMoreClick(data) } }
          },
          data.label || '下一页'
        )
      }

      const isVehicle = data && data.vehicleNo != null
      const isGroup = !isVehicle && isGarageGroupNode(data)
      const children = []

      if (isGroup) {
        children.push(
          h('i', { class: 'gvt-tree__icon gvt-tree__icon--folder el-icon-folder', attrs: { 'aria-hidden': 'true' } })
        )
        const name = data.displayName || data.teamName || data.name || data.label || ''
        const countLabel = this.buildGroupCountLabel(data)
        children.push(h('span', { class: 'gvt-tree__group-name' }, name))
        if (countLabel) {
          children.push(h('span', { class: 'gvt-tree__group-count' }, ` [${countLabel}]`))
        }
      } else if (isVehicle) {
        children.push(renderGarageVehicleTreeIcon(h, 'gvt-tree__vehicle-icon'))
        children.push(h('span', { class: 'gvt-tree__plate' }, String(data.vehicleNo || '')))
      } else {
        children.push(h('span', { class: 'gvt-tree__plain' }, node.label))
      }

      return h('span', { class: 'gvt-tree__node' }, children)
    },
    buildGroupCountLabel(data) {
      if (!data || typeof data !== 'object') return ''
      const vc = data.vehicleCount != null ? Number(data.vehicleCount) : null
      const tvc = data.totalVehicleCount != null ? Number(data.totalVehicleCount) : null
      if (vc != null && tvc != null && Number.isFinite(vc) && Number.isFinite(tvc)) {
        return `${vc}/${tvc}`
      }
      const count = tvc != null && Number.isFinite(tvc) ? tvc : vc != null && Number.isFinite(vc) ? vc : null
      return count != null ? String(count) : ''
    },
    async handleLoadMoreClick(data) {
      if (this.disabled || this.garageLoadMoreLoading || !this.treeLoader) return
      this.garageLoadMoreLoading = true
      try {
        this.vehicleTreeData = await this.treeLoader.handleGarageLoadMore(this.vehicleTreeData, data)
      } catch (e) {
        console.error('加载下一页失败', e)
        this.$message.error('加载下一页失败')
      } finally {
        this.garageLoadMoreLoading = false
      }
    },
    emitVehicleFromNode(data) {
      if (!data || data.vehicleNo == null) return
      const plate = String(data.vehicleNo).trim()
      if (!plate) return
      if (!isLicensePlateFormatValid(plate)) {
        this.$message.warning(`车牌格式不正确：${plate}`)
        return
      }
      const payload = {
        value: plate,
        vehicleNo: plate,
        vehiclePlateColor: resolveGarageVehicleColor(data),
        node: data
      }
      this.$emit('vehicle-select', payload)
      this.$emit('apply-vehicle', { vclN: plate, vco: payload.vehiclePlateColor })
    },
    onTreeNodeClick(data) {
      if (!data) {
        this.selectedNode = null
        return
      }
      if (data.type === GARAGE_NODE_LOAD_MORE) {
        void this.handleLoadMoreClick(data)
        return
      }
      this.selectedNode = data
      if (data.vehicleNo != null) {
        this.$nextTick(() => {
          const tree = this.$refs.garageTree
          if (tree && typeof tree.setCurrentKey === 'function') {
            tree.setCurrentKey(data.id)
          }
        })
        this.emitVehicleFromNode(data)
      }
    },
    onTreeCheckChange(data, checked) {
      if (this.suppressCheckChange) return
      if (!this.showCheckbox || !data || data.type === GARAGE_NODE_LOAD_MORE || data.vehicleNo == null) return
      const plate = String(data.vehicleNo || '').trim()
      if (!plate) return
      this.$emit('vehicle-check-change', {
        checked: !!checked,
        vehicles: [{ node: data, vclN: plate, vco: resolveGarageVehicleColor(data) }]
      })
    },
    onTreeCheck(data, checkedInfo) {
      if (!this.showCheckbox || this.suppressCheckChange) return
      if (!data || data.type === GARAGE_NODE_LOAD_MORE || data.vehicleNo != null) return
      const checkedNodes = checkedInfo && Array.isArray(checkedInfo.checkedNodes) ? checkedInfo.checkedNodes : []
      const checkedKeySet = new Set(
        checkedNodes.map((n) => (n && n.id != null ? String(n.id) : '')).filter(Boolean)
      )
      const checked =
        data && data.id != null && checkedKeySet.has(String(data.id))
          ? true
          : !!(this.$refs.garageTree &&
            typeof this.$refs.garageTree.getCheckedKeys === 'function' &&
            this.$refs.garageTree.getCheckedKeys().map((k) => String(k)).includes(String(data.id)))
      const vehicles = collectGarageVehicleNodes([data])
        .map((node) => ({
          node,
          vclN: String(node.vehicleNo || '').trim(),
          vco: resolveGarageVehicleColor(node)
        }))
        .filter((v) => v.vclN)
      if (!vehicles.length) return
      this.suppressCheckChange = true
      setTimeout(() => {
        this.suppressCheckChange = false
      }, 0)
      this.$emit('vehicle-check-change', { checked, vehicles })
    },
    onTreeWrapClick(e) {
      try {
        const target = e && e.target ? e.target : null
        const hitNodeContent = target && target.closest ? target.closest('.el-tree-node__content') : null
        if (hitNodeContent) return
      } catch (err) {
        /* ignore */
      }
      this.selectedNode = null
      const tree = this.$refs.garageTree
      if (tree && typeof tree.setCurrentKey === 'function') {
        try {
          tree.setCurrentKey(null)
        } catch (err2) {
          /* ignore */
        }
      }
    },
    expandToNode(nodeId) {
      const tree = this.$refs.garageTree
      if (!tree || typeof tree.getNode !== 'function' || nodeId == null) return
      const targetNode = tree.getNode(nodeId)
      if (!targetNode) return
      let cur = targetNode
      while (cur) {
        cur.expanded = true
        cur = cur.parent
      }
      if (typeof tree.setCurrentKey === 'function') {
        tree.setCurrentKey(nodeId)
      }
    },
    selectVehicleByPlate(plate) {
      const q = plate != null ? String(plate).trim().toUpperCase() : ''
      if (!q) return null
      const all = collectGarageVehicleNodes(this.vehicleTreeData)
      const hit = all.find((n) => String(n.vehicleNo || '').toUpperCase() === q)
      if (!hit) return null
      this.selectedNode = hit
      this.$nextTick(() => this.expandToNode(hit.id))
      return hit
    },
    getTreeData() {
      return this.vehicleTreeData
    },
    findNodeById(id) {
      return findGarageNodeById(this.vehicleTreeData, id)
    }
  }
}
</script>

<style scoped>
.gvt-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.gvt-panel__toolbar {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.5rem;
  flex-shrink: 0;
}

.gvt-panel__search {
  flex: 1;
  min-width: 0;
}

.gvt-panel__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: none;
  border-radius: 0.25rem;
  background: transparent;
  color: var(--layout-primary, #087dff);
  cursor: pointer;
  flex-shrink: 0;
}

.gvt-panel__icon-btn:hover:not(:disabled) {
  background: var(--layout-primary-soft, rgba(8, 125, 255, 0.08));
}

.gvt-panel__icon-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.gvt-panel__spin {
  animation: gvt-spin 0.8s linear infinite;
}

@keyframes gvt-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.gvt-panel__tree-wrap {
  flex: 1;
  min-height: 8rem;
  overflow: auto;
  border: 0.0625rem solid #ebeef5;
  border-radius: 0.25rem;
  padding: 0.25rem 0.125rem;
  position: relative;
}

.gvt-panel--compact .gvt-panel__tree-wrap {
  min-height: 6rem;
  max-height: 16rem;
}

.gvt-panel__empty {
  padding: 1.5rem 0;
  text-align: center;
  color: #909399;
  font-size: 0.8125rem;
}

.gvt-panel__tree {
  font-size: 0.8125rem;
  background: #fff;
}

.gvt-panel__tree >>> .el-tree-node__content {
  height: 1.625rem;
  line-height: 1.625rem;
  padding-right: 0.25rem;
}

.gvt-panel__tree >>> .el-tree-node.is-current > .el-tree-node__content {
  background-color: var(--layout-primary-soft, rgba(8, 125, 255, 0.08)) !important;
}

.gvt-panel__tree >>> .el-tree-node__content:hover {
  background-color: #f5f7fa;
}

/* 蓝色展开三角 */
.gvt-panel__tree >>> .el-tree-node__expand-icon {
  color: var(--layout-primary, #087dff);
  font-size: 0.75rem;
  padding: 0;
  margin-right: 0.125rem;
}

.gvt-panel__tree >>> .el-tree-node__expand-icon.is-leaf {
  color: transparent;
  pointer-events: none;
}

/* 层级虚线 */
.gvt-panel__tree >>> .el-tree-node__children {
  position: relative;
}

.gvt-panel__tree >>> .el-tree-node__children::before {
  content: '';
  position: absolute;
  left: 0.4375rem;
  top: 0;
  bottom: 0.5rem;
  border-left: 1px dotted #c0c4cc;
  pointer-events: none;
}

.gvt-panel__tree >>> .el-checkbox {
  margin-right: 0.375rem;
}

.gvt-panel__tree >>> .el-checkbox__inner:hover,
.gvt-panel__tree >>> .el-checkbox__input.is-focus .el-checkbox__inner {
  border-color: var(--layout-primary, #087dff);
}

.gvt-panel__tree >>> .el-checkbox__input.is-checked .el-checkbox__inner,
.gvt-panel__tree >>> .el-checkbox__input.is-indeterminate .el-checkbox__inner {
  background-color: var(--layout-primary, #087dff);
  border-color: var(--layout-primary, #087dff);
}

.gvt-panel__tree >>> .el-checkbox__input.is-checked .el-checkbox__inner:hover,
.gvt-panel__tree >>> .el-checkbox__input.is-indeterminate .el-checkbox__inner:hover {
  background-color: var(--layout-primary-hover, #0670e6);
  border-color: var(--layout-primary-hover, #0670e6);
}

.gvt-panel__tree >>> .el-checkbox__inner {
  width: 0.875rem;
  height: 0.875rem;
}

.gvt-panel__tree >>> .el-checkbox__inner::after {
  left: 0.2rem;
  top: 0.05rem;
}

/* render-content 节点样式（须用 >>>，否则 scoped 不作用于 h() 创建的元素） */
.gvt-panel__tree >>> .gvt-tree__node {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  max-width: calc(100% - 0.5rem);
  overflow: hidden;
  vertical-align: middle;
}

.gvt-panel__tree >>> .gvt-tree__icon--folder {
  color: #e6a23c;
  font-size: 1rem;
  flex-shrink: 0;
}

.gvt-panel__tree >>> .gvt-tree__group-name {
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gvt-panel__tree >>> .gvt-tree__group-count {
  color: #909399;
  flex-shrink: 0;
}

.gvt-panel__tree >>> .gvt-tree__vehicle-icon {
  width: 1.125rem;
  height: 1.125rem;
  min-width: 1.125rem;
  flex-shrink: 0;
  object-fit: contain;
  vertical-align: middle;
}

.gvt-panel__tree >>> .gvt-tree__plate {
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gvt-panel__tree >>> .gvt-tree__load-more {
  color: var(--layout-primary, #087dff);
  cursor: pointer;
  user-select: none;
}

.gvt-panel__tree >>> .gvt-tree__load-more:hover {
  text-decoration: underline;
}
</style>
